import { useState, useEffect } from "react";
import {
  ScrollView,
  View,
  Text,
  TextInput,
  Pressable,
  ActivityIndicator,
  Image,
  LayoutAnimation,
} from "react-native";
import { useRouter } from "expo-router";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db, functions } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { colors, radius } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import { useAlert } from "@/hooks/useAlert";
import AlertSheet from "@/components/AlertSheet";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmSheet from "@/components/ConfirmSheet";

interface ScrapedFixture {
  playCricketMatchId: string;
  date: string;
  time: string;
  homeTeam: string;
  awayTeam: string;
  ground: string;
}

interface ScrapedOpponent {
  name: string;
  logoUrl: string | null;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function stripToXI(full: string): string {
  // "Peterborough Town CC - 1st XI" → "1st XI" (for our team names)
  // Use first " - " since club name always comes before the dash
  const dash = full.indexOf(" - ");
  if (dash > 0) return full.slice(dash + 3).trim();
  return full;
}

function stripToClubName(full: string): string {
  // "Horton House CC - 1st XI" → "Horton House CC" (for opponent display)
  const dash = full.lastIndexOf(" - ");
  if (dash > 0) return full.slice(0, dash).trim();
  return full;
}

export default function ImportFixturesScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { club } = useClub();
  const { alertProps, alert } = useAlert();

  const [url, setUrl] = useState((club as any)?.playCricketImportUrl || "");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [step, setStep] = useState<"url" | "map-teams" | "preview" | "sync">("url");

  // Scraped data
  const [fixtures, setFixtures] = useState<ScrapedFixture[]>([]);
  const [allTeamNames, setAllTeamNames] = useState<string[]>([]);
  const [serverMyTeams, setServerMyTeams] = useState<string[]>([]);
  const [opponents, setOpponents] = useState<ScrapedOpponent[]>([]);
  const [clubBadgeUrl, setClubBadgeUrl] = useState<string | null>(null);
  const [logoSaved, setLogoSaved] = useState(false);

  // Existing data
  const [existingTeams, setExistingTeams] = useState<string[]>([]);
  const [existingMatchMap, setExistingMatchMap] = useState<Map<string, string>>(
    new Map(),
  ); // playCricketMatchId → firestoreDocId
  const [savedMappings, setSavedMappings] = useState<Record<string, string>>(
    {},
  ); // playCricketTeamName → name

  // Sync state
  const [existingMatchData, setExistingMatchData] = useState<Array<{ docId: string; team: string; opponent: string; date: string; playCricketMatchId?: string }>>([]);
  const [orphanedFixtures, setOrphanedFixtures] = useState<Array<{ docId: string; team: string; opponent: string; date: string; playCricketMatchId: string }>>([]);
  const [manualFixtures, setManualFixtures] = useState<Array<{ docId: string; team: string; opponent: string; date: string }>>([]);
  const [orphanedTeams, setOrphanedTeams] = useState<Array<{ slug: string; name: string }>>([]);
  const [selectedOrphanedFixtures, setSelectedOrphanedFixtures] = useState<Set<string>>(new Set());
  const [selectedManualFixtures, setSelectedManualFixtures] = useState<Set<string>>(new Set());
  const [selectedOrphanedTeams, setSelectedOrphanedTeams] = useState<Set<string>>(new Set());
  const [syncing, setSyncing] = useState(false);

  // Collapsible section state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    orphanedFixtures: true,
    manualFixtures: false,
    orphanedTeams: true,
    willCreate: false,
    willUpdate: false,
  });

  const { confirmProps, confirm } = useConfirm();

  // User selections
  const [selectedMyTeams, setSelectedMyTeams] = useState<Set<string>>(
    new Set(),
  );
  const [teamMap, setTeamMap] = useState<Record<string, string>>({}); // pcName → appTeamName

  useEffect(() => {
    if (!club) return;
    (async () => {
      setExistingTeams(club.teams || []);
      const tcSnap = await getDocs(
        collection(db, "clubs", club.id, "teamConfig"),
      );
      const saved: Record<string, string> = {};
      tcSnap.docs.forEach((d) => {
        const data = d.data();
        if (data.playCricketTeamName)
          saved[data.playCricketTeamName] = data.name;
      });
      setSavedMappings(saved);

      const matchesSnap = await getDocs(
        collection(db, "clubs", club.id, "matches"),
      );
      const map = new Map<string, string>();
      const matchData: Array<{ docId: string; team: string; opponent: string; date: string; playCricketMatchId?: string }> = [];
      matchesSnap.docs.forEach((d) => {
        const data = d.data();
        const mid = data.playCricketMatchId;
        if (mid) map.set(mid, d.id);
        matchData.push({
          docId: d.id,
          team: data.team || "",
          opponent: data.opponent || "",
          date: data.date || "",
          playCricketMatchId: mid || undefined,
        });
      });
      setExistingMatchMap(map);
      setExistingMatchData(matchData);
    })();
  }, [club?.id]);

  const handleScrape = async () => {
    if (!url.trim()) return;
    setLoading(true);
    try {
      const fn = httpsCallable(functions, "importPlayCricketFixtures");
      const result = await fn({ url: url.trim() });
      const data = result.data as any;
      setFixtures(data.fixtures || []);
      setOpponents(data.opponents || []);
      setClubBadgeUrl(data.clubBadgeUrl || null);

      // Always update club logo from Play-Cricket
      if (data.clubBadgeUrl && club) {
        updateDoc(doc(db, "clubs", club.id), {
          logoUrl: data.clubBadgeUrl,
        }).catch(() => {});
      }

      // Server identified "my teams" via badge matching
      const smt: string[] = data.myTeams || [];
      setServerMyTeams(smt);

      // Also include any with saved mappings
      const freq: Record<string, number> = {};
      for (const f of data.fixtures || []) {
        freq[f.homeTeam] = (freq[f.homeTeam] || 0) + 1;
        freq[f.awayTeam] = (freq[f.awayTeam] || 0) + 1;
      }
      const allNames = Object.entries(freq)
        .sort((a, b) => b[1] - a[1])
        .map(([name]) => name);
      setAllTeamNames(allNames);

      // Pre-select server-identified teams + saved mappings
      const preSelected = new Set<string>(serverMyTeams);
      for (const name of allNames) {
        if (savedMappings[name]) preSelected.add(name);
      }
      setSelectedMyTeams(preSelected);

      // Build initial map from identified teams
      const teamsToMap = smt.length > 0 ? smt : [];
      const map: Record<string, string> = {};
      for (const pcName of teamsToMap) {
        if (savedMappings[pcName]) {
          map[pcName] = savedMappings[pcName];
        } else {
          const stripped = stripToXI(pcName);
          const match = existingTeams.find(
            (t) => t.toLowerCase() === stripped.toLowerCase(),
          );
          map[pcName] = match || stripped;
        }
      }
      setTeamMap(map);
      setSelectedMyTeams(new Set(teamsToMap));

      setStep("map-teams");

      // Save the URL for next time
      if (club) {
        updateDoc(doc(db, "clubs", club.id), {
          playCricketImportUrl: url.trim(),
        }).catch(() => {});
      }
    } catch (err: any) {
      await alert({
        title: "Import failed",
        message: err?.message || "Could not fetch fixtures.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Compute import data
  const myPcNames = [...selectedMyTeams];
  const myFixtures = fixtures.filter(
    (f) => myPcNames.includes(f.homeTeam) || myPcNames.includes(f.awayTeam),
  );

  const newFixtures = myFixtures.filter(
    (f) => !existingMatchMap.has(f.playCricketMatchId),
  );
  const updateFixtures = myFixtures.filter((f) =>
    existingMatchMap.has(f.playCricketMatchId),
  );
  const fixturesForImport = myFixtures;
  const newTeamNames = Object.entries(teamMap)
    .filter(([, appName]) => !existingTeams.includes(appName))
    .map(([, appName]) => appName);
  const opponentsToSave = opponents.filter((o) => !myPcNames.includes(o.name));

  const toggleSection = (key: string) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const computeSyncDiff = () => {
    // Use full Play-Cricket names as-is (source of truth)
    const syncTeamMap: Record<string, string> = {};
    for (const pcName of selectedMyTeams) {
      syncTeamMap[pcName] = pcName;
    }
    setTeamMap(syncTeamMap);

    // Also select all scraped teams as myPcNames
    const syncMyPcNames = [...selectedMyTeams];

    const scrapedIds = new Set(fixtures.map((f) => f.playCricketMatchId));

    // Orphaned fixtures: in app with playCricketMatchId NOT in scraped results
    const orphaned = existingMatchData
      .filter((m) => m.playCricketMatchId && !scrapedIds.has(m.playCricketMatchId))
      .map((m) => ({
        docId: m.docId,
        team: m.team,
        opponent: m.opponent,
        date: m.date,
        playCricketMatchId: m.playCricketMatchId!,
      }));
    setOrphanedFixtures(orphaned);

    // Manual fixtures: in app WITHOUT playCricketMatchId
    const manual = existingMatchData
      .filter((m) => !m.playCricketMatchId)
      .map((m) => ({
        docId: m.docId,
        team: m.team,
        opponent: m.opponent,
        date: m.date,
      }));
    setManualFixtures(manual);

    // Orphaned teams: teams in club.teams not referenced by any scraped fixture AND not by non-orphaned existing fixtures
    const orphanedDocIds = new Set(orphaned.map((o) => o.docId));
    const nonOrphanedExisting = existingMatchData.filter(
      (m) => !orphanedDocIds.has(m.docId),
    );
    const teamsReferencedByScraped = new Set<string>();
    const syncMyFixtures = fixtures.filter((f) => syncMyPcNames.includes(f.homeTeam) || syncMyPcNames.includes(f.awayTeam));
    for (const f of syncMyFixtures) {
      const isHome = syncMyPcNames.includes(f.homeTeam);
      const myPcTeam = isHome ? f.homeTeam : f.awayTeam;
      const appTeam = syncTeamMap[myPcTeam];
      if (appTeam) teamsReferencedByScraped.add(appTeam);
    }
    const teamsReferencedByNonOrphaned = new Set<string>();
    for (const m of nonOrphanedExisting) {
      if (m.team) teamsReferencedByNonOrphaned.add(m.team);
    }

    const clubTeams = club?.teams || [];
    const orphTeams = clubTeams
      .filter(
        (t: string) =>
          !teamsReferencedByScraped.has(t) &&
          !teamsReferencedByNonOrphaned.has(t),
      )
      .map((t: string) => ({ slug: slugify(t), name: t }));
    setOrphanedTeams(orphTeams);

    // Pre-select all for deletion
    setSelectedOrphanedFixtures(new Set(orphaned.map((o) => o.docId)));
    setSelectedOrphanedTeams(new Set(orphTeams.map((t: { slug: string }) => t.slug)));
  };

  const handleSyncAction = async () => {
    if (!club || !user) return;
    const allFixturesToDelete = [...selectedOrphanedFixtures, ...selectedManualFixtures];
    const fixtureCount = allFixturesToDelete.length;
    const teamCount = selectedOrphanedTeams.size;

    const confirmed = await confirm({
      title: "Confirm sync",
      message: `This will permanently delete ${fixtureCount} fixture${fixtureCount !== 1 ? "s" : ""} and ${teamCount} team${teamCount !== 1 ? "s" : ""}. This cannot be undone.`,
      confirmLabel: "Delete & sync",
      destructive: true,
    });
    if (!confirmed) return;

    setSyncing(true);
    try {
      // 1. Delete orphaned + selected manual fixtures and teams via cloud function
      const fn = httpsCallable(functions, "syncDeleteFixtures");
      await fn({
        clubId: club.id,
        matchIds: allFixturesToDelete,
        teamSlugs: [...selectedOrphanedTeams],
      });

      // 2. Run the normal import for creates/updates
      await handleImport();
    } catch (err: any) {
      await alert({
        title: "Sync failed",
        message: err?.message || "Something went wrong during sync.",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleImport = async () => {
    if (!club || !user) return;
    setImporting(true);
    try {
      const leadDays = club.availabilityLeadDays ?? 7;
      const isSyncMode = step === "sync";
      // In sync mode: only Play-Cricket teams. In import mode: merge with existing.
      const allAppTeams = isSyncMode
        ? [...new Set(Object.values(teamMap))]
        : [...new Set([...existingTeams, ...newTeamNames])];

      // 1. Create/update team configs
      for (const [pcName, appName] of Object.entries(teamMap)) {
        const slug = slugify(appName);
        await setDoc(
          doc(db, "clubs", club.id, "teamConfig", slug),
          {
            name: appName,
            playCricketTeamName: pcName,
            priority: allAppTeams.indexOf(appName) + 1,
            captainId: "",
            captainName: "",
            viceCaptainId: "",
            viceCaptainName: "",
          },
          { merge: true },
        );
      }
      // Update club.teams
      await updateDoc(doc(db, "clubs", club.id), { teams: allAppTeams });

      // 2. Save opponents (parallel, batched)
      const oppBatches = [];
      for (let i = 0; i < opponentsToSave.length; i += 10) {
        oppBatches.push(opponentsToSave.slice(i, i + 10));
      }
      for (const batch of oppBatches) {
        await Promise.all(
          batch.map((opp) =>
            setDoc(
              doc(db, "clubs", club.id, "opponents", slugify(opp.name)),
              {
                name: opp.name,
                logoUrl: opp.logoUrl || null,
              },
              { merge: true },
            ),
          ),
        );
      }

      // 3. Create new fixtures + update existing ones
      let created = 0;
      let updated = 0;
      for (const f of fixturesForImport) {
        const isHome = myPcNames.includes(f.homeTeam);
        const myPcTeam = isHome ? f.homeTeam : f.awayTeam;
        const oppPcName = isHome ? f.awayTeam : f.homeTeam;
        const teamName = teamMap[myPcTeam];
        if (!teamName) continue;

        const oppDisplay = stripToClubName(oppPcName);
        const oppData = opponents.find((o) => o.name === oppPcName);
        const existingDocId = existingMatchMap.get(f.playCricketMatchId);

        // Fields that come from Play-Cricket (safe to overwrite on re-import)
        const pcFields = {
          team: teamName,
          opponent: oppDisplay || oppPcName,
          opponentLogoUrl: oppData?.logoUrl || null,
          date: f.date,
          time: f.time || "12:00",
          venue: isHome ? "home" : "away",
          location: f.ground || "",
          playCricketMatchId: f.playCricketMatchId,
        };

        if (existingDocId) {
          // Update — only overwrite Play-Cricket fields, keep app state (status, availability, fees)
          await updateDoc(
            doc(db, "clubs", club.id, "matches", existingDocId),
            pcFields,
          );
          updated++;
        } else {
          // New fixture
          const matchDate = new Date(f.date + "T00:00:00");
          matchDate.setDate(matchDate.getDate() - leadDays);
          const opensAt = matchDate.toISOString().slice(0, 10);

          await addDoc(collection(db, "clubs", club.id, "matches"), {
            ...pcFields,
            type: "league",
            status: "availability",
            availabilityOpensAt: opensAt,
            availabilityNotifiedAt: null,
            actingCaptainId: null,
            actingCaptainName: null,
            proposedBy: null,
            confirmedBy: null,
            clubId: club.id,
            clubName: club.name,
            createdBy: user.uid,
            createdAt: Timestamp.now(),
          });
          created++;
        }
      }

      await alert({
        title: "Import complete",
        emoji: "🏏",
        message: `${created} new, ${updated} updated${newTeamNames.length > 0 ? `, ${newTeamNames.length} new teams` : ""}, ${opponentsToSave.length} opponents.`,
      });
      router.back();
    } catch (err: any) {
      await alert({
        title: "Import failed",
        message: err?.message || "Something went wrong.",
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.bg }}>
      <ContentWidth>
        <View style={{ padding: 20 }}>
          {/* Step 1: URL */}
          {step === "url" && (
            <>
              <Text
                style={{
                  fontSize: 22,
                  fontWeight: "800",
                  color: colors.ink,
                  marginBottom: 8,
                }}
              >
                Import from Play-Cricket
              </Text>
              <Text
                style={{
                  fontSize: 14,
                  color: colors.inkMuted,
                  marginBottom: 20,
                  lineHeight: 20,
                }}
              >
                Go to your club's Play-Cricket Matches page, select the current
                season, switch to "Fixture" tab and "View by year", then paste
                the URL.
              </Text>
              <TextInput
                value={url}
                onChangeText={setUrl}
                placeholder="Paste the Play-Cricket URL here..."
                placeholderTextColor={colors.inkMuted}
                autoCapitalize="none"
                multiline
                style={{
                  backgroundColor: colors.white,
                  borderRadius: radius.md,
                  borderWidth: 1,
                  borderColor: colors.line,
                  padding: 14,
                  fontSize: 14,
                  color: colors.ink,
                  minHeight: 80,
                }}
              />
              <Pressable
                onPress={handleScrape}
                disabled={loading || !url.trim()}
                style={({ pressed }) => ({
                  backgroundColor: colors.brand,
                  borderRadius: radius.lg,
                  padding: 16,
                  alignItems: "center",
                  marginTop: 20,
                  opacity: pressed || loading ? 0.8 : 1,
                })}
              >
                {loading ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text
                    style={{ color: "#fff", fontSize: 16, fontWeight: "700" }}
                  >
                    Fetch fixtures
                  </Text>
                )}
              </Pressable>

              {clubBadgeUrl && (
                <Pressable
                  onPress={async () => {
                    if (!club) return;
                    await updateDoc(doc(db, "clubs", club.id), {
                      logoUrl: clubBadgeUrl,
                    });
                    setLogoSaved(true);
                    setTimeout(() => setLogoSaved(false), 3000);
                  }}
                  disabled={logoSaved}
                  style={({ pressed }) => ({
                    borderWidth: 2,
                    borderColor: logoSaved ? colors.brand : colors.line,
                    borderRadius: radius.lg,
                    padding: 14,
                    alignItems: "center",
                    marginTop: 12,
                    flexDirection: "row",
                    justifyContent: "center",
                    gap: 10,
                    opacity: pressed ? 0.8 : 1,
                  })}
                >
                  <Image
                    source={{ uri: clubBadgeUrl }}
                    style={{ width: 24, height: 24, borderRadius: 4 }}
                  />
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: "700",
                      color: logoSaved ? colors.brand : colors.ink,
                    }}
                  >
                    {logoSaved ? "Logo saved ✓" : "Import club logo"}
                  </Text>
                </Pressable>
              )}
            </>
          )}

          {/* Step 2: Map teams to app teams */}
          {step === "map-teams" && (
            <>
              <Text
                style={{
                  fontSize: 22,
                  fontWeight: "800",
                  color: colors.ink,
                  marginBottom: 4,
                }}
              >
                Name your teams
              </Text>
              <Text
                style={{
                  fontSize: 14,
                  color: colors.inkMuted,
                  marginBottom: 16,
                  lineHeight: 20,
                }}
              >
                Choose how each Play-Cricket team appears in your app. We've
                suggested names — edit if needed.
              </Text>

              {myPcNames.map((pcName) => {
                const appName = teamMap[pcName] || stripToXI(pcName);
                const matchedExisting = existingTeams.includes(appName);
                const fixtureCount = fixtures.filter(
                  (f) => f.homeTeam === pcName || f.awayTeam === pcName,
                ).length;
                return (
                  <View
                    key={pcName}
                    style={{
                      backgroundColor: colors.white,
                      borderRadius: radius.md,
                      borderWidth: 1,
                      borderColor: matchedExisting
                        ? colors.brandLight
                        : colors.line,
                      padding: 16,
                      marginBottom: 12,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 16,
                        fontWeight: "800",
                        color: colors.ink,
                      }}
                    >
                      {pcName}
                    </Text>
                    <Text
                      style={{
                        fontSize: 12,
                        color: colors.inkMuted,
                        marginTop: 2,
                        marginBottom: 14,
                      }}
                    >
                      {fixtureCount} fixtures
                    </Text>

                    {existingTeams.length > 0 &&
                      (() => {
                        // Hide teams already matched by OTHER cards
                        const takenByOthers = new Set(
                          Object.entries(teamMap)
                            .filter(
                              ([k, v]) =>
                                k !== pcName && existingTeams.includes(v),
                            )
                            .map(([, v]) => v),
                        );
                        const available = existingTeams.filter(
                          (t) => !takenByOthers.has(t),
                        );
                        return available.length > 0 || true; // always render the section for "+ New team"
                      })() && (
                        <>
                          <Text
                            style={{
                              fontSize: 12,
                              fontWeight: "700",
                              color: colors.inkMuted,
                              marginBottom: 8,
                            }}
                          >
                            Match to an existing team to avoid duplicates:
                          </Text>
                          <View
                            style={{
                              flexDirection: "row",
                              flexWrap: "wrap",
                              gap: 8,
                              marginBottom: 14,
                            }}
                          >
                            {existingTeams
                              .filter((t) => {
                                // Hide if taken by another card
                                const takenBy = Object.entries(teamMap).find(
                                  ([k, v]) => k !== pcName && v === t,
                                );
                                return !takenBy;
                              })
                              .map((t) => (
                                <Pressable
                                  key={t}
                                  onPress={() =>
                                    setTeamMap((prev) => ({
                                      ...prev,
                                      [pcName]: t,
                                    }))
                                  }
                                  style={({ pressed }) => ({
                                    paddingHorizontal: 14,
                                    paddingVertical: 8,
                                    borderRadius: radius.md,
                                    backgroundColor:
                                      appName === t
                                        ? colors.brand
                                        : colors.white,
                                    borderWidth: 1.5,
                                    borderColor:
                                      appName === t
                                        ? colors.brand
                                        : colors.line,
                                    opacity: pressed ? 0.7 : 1,
                                  })}
                                >
                                  <Text
                                    style={{
                                      fontSize: 13,
                                      fontWeight: "700",
                                      color:
                                        appName === t ? "#fff" : colors.ink,
                                    }}
                                  >
                                    {t}
                                  </Text>
                                </Pressable>
                              ))}
                            <Pressable
                              onPress={() =>
                                setTeamMap((prev) => ({
                                  ...prev,
                                  [pcName]: stripToXI(pcName),
                                }))
                              }
                              style={({ pressed }) => ({
                                paddingHorizontal: 14,
                                paddingVertical: 8,
                                borderRadius: radius.md,
                                backgroundColor: !matchedExisting
                                  ? colors.amber50
                                  : colors.white,
                                borderWidth: 1.5,
                                borderColor: !matchedExisting
                                  ? colors.amber200
                                  : colors.line,
                                opacity: pressed ? 0.7 : 1,
                              })}
                            >
                              <Text
                                style={{
                                  fontSize: 13,
                                  fontWeight: "700",
                                  color: !matchedExisting
                                    ? colors.amber
                                    : colors.ink,
                                }}
                              >
                                + New team
                              </Text>
                            </Pressable>
                          </View>
                        </>
                      )}

                    {!matchedExisting && (
                      <View>
                        <Text
                          style={{
                            fontSize: 12,
                            fontWeight: "700",
                            color: colors.inkMuted,
                            marginBottom: 6,
                          }}
                        >
                          Team name in app:
                        </Text>
                        <TextInput
                          value={appName}
                          onChangeText={(v) =>
                            setTeamMap((prev) => ({ ...prev, [pcName]: v }))
                          }
                          style={{
                            fontSize: 15,
                            fontWeight: "700",
                            color: colors.ink,
                            backgroundColor: colors.bg,
                            borderRadius: radius.md,
                            borderWidth: 1,
                            borderColor: colors.line,
                            paddingHorizontal: 12,
                            paddingVertical: 10,
                          }}
                        />
                      </View>
                    )}

                    {matchedExisting && (
                      <View
                        style={{
                          backgroundColor: colors.brand50,
                          borderRadius: 8,
                          padding: 10,
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Text style={{ fontSize: 14 }}>✓</Text>
                        <Text
                          style={{
                            fontSize: 13,
                            fontWeight: "700",
                            color: colors.brandDark,
                          }}
                        >
                          Linked to "{appName}"
                        </Text>
                      </View>
                    )}
                  </View>
                );
              })}

              <View style={{ flexDirection: "row", gap: 12, marginTop: 8 }}>
                <Pressable
                  onPress={() => setStep("url")}
                  style={({ pressed }) => ({
                    flex: 1,
                    padding: 14,
                    borderRadius: radius.md,
                    borderWidth: 1,
                    borderColor: colors.line,
                    alignItems: "center",
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: "700",
                      color: colors.inkMuted,
                    }}
                  >
                    Back
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => setStep("preview")}
                  style={({ pressed }) => ({
                    flex: 2,
                    padding: 14,
                    borderRadius: radius.md,
                    backgroundColor: colors.brand,
                    alignItems: "center",
                    opacity: pressed ? 0.8 : 1,
                  })}
                >
                  <Text
                    style={{ fontSize: 14, fontWeight: "700", color: "#fff" }}
                  >
                    Preview import
                  </Text>
                </Pressable>
              </View>

              <Pressable
                onPress={() => {
                  computeSyncDiff();
                  setStep("sync");
                }}
              >
                <View
                  style={{
                    borderWidth: 2,
                    borderColor: colors.danger,
                    borderRadius: radius.lg,
                    padding: 16,
                    alignItems: "center",
                    marginTop: 12,
                  }}
                >
                  <Text
                    style={{
                      color: colors.danger,
                      fontWeight: "700",
                      fontSize: 14,
                    }}
                  >
                    Sync with Play-Cricket
                  </Text>
                  <Text
                    style={{
                      color: colors.inkMuted,
                      fontSize: 12,
                      marginTop: 4,
                    }}
                  >
                    Delete orphaned fixtures and teams
                  </Text>
                </View>
              </Pressable>
            </>
          )}

          {/* Step 4: Preview */}
          {step === "preview" && (
            <>
              <Text
                style={{
                  fontSize: 22,
                  fontWeight: "800",
                  color: colors.ink,
                  marginBottom: 16,
                }}
              >
                Ready to import
              </Text>

              {newTeamNames.length > 0 && (
                <View
                  style={{
                    backgroundColor: colors.brand50,
                    borderRadius: radius.md,
                    padding: 14,
                    marginBottom: 16,
                    borderWidth: 1,
                    borderColor: colors.brandLight,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 12,
                      fontWeight: "800",
                      color: colors.brandDark,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      marginBottom: 6,
                    }}
                  >
                    New teams
                  </Text>
                  {newTeamNames.map((t) => (
                    <Text
                      key={t}
                      style={{
                        fontSize: 15,
                        fontWeight: "700",
                        color: colors.ink,
                        marginBottom: 2,
                      }}
                    >
                      + {t}
                    </Text>
                  ))}
                </View>
              )}

              <View
                style={{
                  backgroundColor: colors.white,
                  borderRadius: radius.md,
                  borderWidth: 1,
                  borderColor: colors.line,
                  padding: 14,
                  marginBottom: 16,
                }}
              >
                <Text
                  style={{ fontSize: 28, fontWeight: "800", color: colors.ink }}
                >
                  {fixturesForImport.length}
                </Text>
                <Text style={{ fontSize: 13, color: colors.inkMuted }}>
                  fixtures total
                </Text>
                {newFixtures.length > 0 && (
                  <Text
                    style={{
                      fontSize: 12,
                      color: colors.brand,
                      fontWeight: "600",
                      marginTop: 4,
                    }}
                  >
                    {newFixtures.length} new
                  </Text>
                )}
                {updateFixtures.length > 0 && (
                  <Text
                    style={{
                      fontSize: 12,
                      color: colors.inkMuted,
                      marginTop: 2,
                    }}
                  >
                    {updateFixtures.length} will be updated
                  </Text>
                )}
              </View>

              {myPcNames.map((pcName) => {
                const teamName = teamMap[pcName];
                if (!teamName) return null;
                const teamFixtures = fixturesForImport.filter(
                  (f) => f.homeTeam === pcName || f.awayTeam === pcName,
                );
                if (teamFixtures.length === 0) return null;
                return (
                  <View key={pcName} style={{ marginBottom: 16 }}>
                    <Text
                      style={{
                        fontSize: 13,
                        fontWeight: "800",
                        color: colors.inkMuted,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        marginBottom: 8,
                      }}
                    >
                      {teamName} ({teamFixtures.length})
                    </Text>
                    <View
                      style={{
                        backgroundColor: colors.white,
                        borderRadius: radius.md,
                        borderWidth: 1,
                        borderColor: colors.line,
                      }}
                    >
                      {teamFixtures.slice(0, 5).map((f, i) => {
                        const isHome = f.homeTeam === pcName;
                        const opp = stripToClubName(
                          isHome ? f.awayTeam : f.homeTeam,
                        );
                        return (
                          <View
                            key={f.playCricketMatchId}
                            style={{
                              padding: 12,
                              borderBottomWidth:
                                i < Math.min(teamFixtures.length, 5) - 1
                                  ? 1
                                  : 0,
                              borderBottomColor: colors.line,
                              flexDirection: "row",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <View style={{ flex: 1 }}>
                              <Text
                                style={{
                                  fontSize: 14,
                                  fontWeight: "700",
                                  color: colors.ink,
                                }}
                              >
                                vs {opp}
                              </Text>
                              <Text
                                style={{ fontSize: 12, color: colors.inkMuted }}
                              >
                                {f.date} · {f.time || "TBC"}
                              </Text>
                            </View>
                            <View
                              style={{
                                backgroundColor: isHome
                                  ? colors.brand50
                                  : colors.amber50,
                                paddingHorizontal: 8,
                                paddingVertical: 3,
                                borderRadius: 999,
                              }}
                            >
                              <Text
                                style={{
                                  fontSize: 10,
                                  fontWeight: "800",
                                  color: isHome
                                    ? colors.brandDark
                                    : colors.amber,
                                }}
                              >
                                {isHome ? "HOME" : "AWAY"}
                              </Text>
                            </View>
                          </View>
                        );
                      })}
                      {teamFixtures.length > 5 && (
                        <View style={{ padding: 10, alignItems: "center" }}>
                          <Text
                            style={{ fontSize: 12, color: colors.inkMuted }}
                          >
                            +{teamFixtures.length - 5} more
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>
                );
              })}

              <Text
                style={{
                  fontSize: 13,
                  color: colors.inkMuted,
                  marginBottom: 16,
                }}
              >
                {opponentsToSave.length} opponents will be saved
              </Text>

              <View style={{ flexDirection: "row", gap: 12 }}>
                <Pressable
                  onPress={() => setStep("map-teams")}
                  style={({ pressed }) => ({
                    flex: 1,
                    padding: 14,
                    borderRadius: radius.md,
                    borderWidth: 1,
                    borderColor: colors.line,
                    alignItems: "center",
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: "700",
                      color: colors.inkMuted,
                    }}
                  >
                    Back
                  </Text>
                </Pressable>
                <Pressable
                  onPress={handleImport}
                  disabled={importing || fixturesForImport.length === 0}
                  style={({ pressed }) => ({
                    flex: 2,
                    padding: 14,
                    borderRadius: radius.md,
                    backgroundColor: colors.brand,
                    alignItems: "center",
                    opacity: pressed || importing ? 0.8 : 1,
                  })}
                >
                  {importing ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <Text
                      style={{ fontSize: 14, fontWeight: "700", color: "#fff" }}
                    >
                      {newFixtures.length > 0 && updateFixtures.length > 0
                        ? `Import ${newFixtures.length} + update ${updateFixtures.length}`
                        : newFixtures.length > 0
                          ? `Import ${newFixtures.length} fixtures`
                          : `Update ${updateFixtures.length} fixtures`}
                    </Text>
                  )}
                </Pressable>
              </View>
            </>
          )}
          {/* Step: Sync */}
          {step === "sync" && (
            <>
              <Text
                style={{
                  fontSize: 22,
                  fontWeight: "800",
                  color: colors.ink,
                  marginBottom: 4,
                }}
              >
                Sync Preview
              </Text>
              <Text
                style={{
                  fontSize: 14,
                  color: colors.inkMuted,
                  marginBottom: 20,
                  lineHeight: 20,
                }}
              >
                Review what will be created, updated, and deleted.
              </Text>

              <Pressable
                onPress={() => setStep("map-teams")}
                style={({ pressed }) => ({
                  padding: 14,
                  borderRadius: radius.md,
                  borderWidth: 1,
                  borderColor: colors.line,
                  alignItems: "center",
                  opacity: pressed ? 0.7 : 1,
                  marginBottom: 20,
                })}
              >
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: "700",
                    color: colors.inkMuted,
                  }}
                >
                  Back
                </Text>
              </Pressable>

              {/* Orphaned Fixtures */}
              <Pressable onPress={() => toggleSection("orphanedFixtures")}>
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: "800",
                    color: colors.danger,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: 8,
                  }}
                >
                  {expandedSections.orphanedFixtures ? "▼" : "▶"} ORPHANED FIXTURES ({orphanedFixtures.length})
                </Text>
              </Pressable>
              {expandedSections.orphanedFixtures && (
                <View
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: radius.md,
                    borderWidth: 1,
                    borderColor: colors.line,
                    marginBottom: 16,
                  }}
                >
                  {orphanedFixtures.length === 0 ? (
                    <View style={{ padding: 14 }}>
                      <Text style={{ fontSize: 13, color: colors.inkMuted }}>
                        No orphaned fixtures found
                      </Text>
                    </View>
                  ) : (
                    <>
                      <View
                        style={{
                          flexDirection: "row",
                          gap: 12,
                          padding: 12,
                          borderBottomWidth: 1,
                          borderBottomColor: colors.line,
                        }}
                      >
                        <Pressable
                          onPress={() =>
                            setSelectedOrphanedFixtures(
                              new Set(orphanedFixtures.map((o) => o.docId)),
                            )
                          }
                        >
                          <Text
                            style={{
                              fontSize: 12,
                              fontWeight: "700",
                              color: colors.brand,
                            }}
                          >
                            Select all
                          </Text>
                        </Pressable>
                        <Pressable
                          onPress={() =>
                            setSelectedOrphanedFixtures(new Set())
                          }
                        >
                          <Text
                            style={{
                              fontSize: 12,
                              fontWeight: "700",
                              color: colors.inkMuted,
                            }}
                          >
                            Deselect all
                          </Text>
                        </Pressable>
                      </View>
                      {orphanedFixtures.map((f, i) => {
                        const selected = selectedOrphanedFixtures.has(f.docId);
                        return (
                          <Pressable
                            key={f.docId}
                            onPress={() => {
                              setSelectedOrphanedFixtures((prev) => {
                                const next = new Set(prev);
                                if (next.has(f.docId)) next.delete(f.docId);
                                else next.add(f.docId);
                                return next;
                              });
                            }}
                            style={{
                              padding: 12,
                              borderBottomWidth:
                                i < orphanedFixtures.length - 1 ? 1 : 0,
                              borderBottomColor: colors.line,
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <View
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                borderWidth: 2,
                                borderColor: selected
                                  ? colors.danger
                                  : colors.line,
                                backgroundColor: selected
                                  ? colors.danger
                                  : "transparent",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {selected && (
                                <Text
                                  style={{
                                    color: "#fff",
                                    fontSize: 12,
                                    fontWeight: "800",
                                  }}
                                >
                                  ✓
                                </Text>
                              )}
                            </View>
                            <Text
                              style={{
                                fontSize: 14,
                                fontWeight: "600",
                                color: colors.ink,
                                flex: 1,
                              }}
                            >
                              {f.team} vs {f.opponent} · {f.date}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </>
                  )}
                </View>
              )}

              {/* Manual Fixtures */}
              <Pressable onPress={() => toggleSection("manualFixtures")}>
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: "800",
                    color: colors.inkMuted,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: 8,
                  }}
                >
                  {expandedSections.manualFixtures ? "▼" : "▶"} MANUAL FIXTURES ({manualFixtures.length})
                </Text>
              </Pressable>
              {expandedSections.manualFixtures && (
                <View
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: radius.md,
                    borderWidth: 1,
                    borderColor: colors.line,
                    marginBottom: 16,
                  }}
                >
                  {manualFixtures.length === 0 ? (
                    <View style={{ padding: 14 }}>
                      <Text style={{ fontSize: 13, color: colors.inkMuted }}>
                        No manual fixtures
                      </Text>
                    </View>
                  ) : (
                    <>
                      <View style={{ padding: 12, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                        <Text style={{ fontSize: 12, color: colors.inkMuted }}>
                          Created manually — select to delete
                        </Text>
                        <Pressable onPress={() => {
                          if (selectedManualFixtures.size === manualFixtures.length) {
                            setSelectedManualFixtures(new Set());
                          } else {
                            setSelectedManualFixtures(new Set(manualFixtures.map((f) => f.docId)));
                          }
                        }}>
                          <Text style={{ fontSize: 12, fontWeight: "700", color: colors.brand }}>
                            {selectedManualFixtures.size === manualFixtures.length ? "Deselect all" : "Select all"}
                          </Text>
                        </Pressable>
                      </View>
                      {manualFixtures.map((f, i) => (
                        <Pressable
                          key={f.docId}
                          onPress={() => {
                            const next = new Set(selectedManualFixtures);
                            next.has(f.docId) ? next.delete(f.docId) : next.add(f.docId);
                            setSelectedManualFixtures(next);
                          }}
                          style={{
                            padding: 12,
                            flexDirection: "row",
                            alignItems: "center",
                            borderBottomWidth:
                              i < manualFixtures.length - 1 ? 1 : 0,
                            borderBottomColor: colors.line,
                          }}
                        >
                          <View style={{
                            width: 20, height: 20, borderRadius: 4, borderWidth: 2,
                            borderColor: selectedManualFixtures.has(f.docId) ? colors.danger : colors.line,
                            backgroundColor: selectedManualFixtures.has(f.docId) ? colors.danger : "transparent",
                            marginRight: 10, alignItems: "center", justifyContent: "center",
                          }}>
                            {selectedManualFixtures.has(f.docId) && (
                              <Text style={{ color: "#fff", fontSize: 12, fontWeight: "800" }}>✓</Text>
                            )}
                          </View>
                          <Text
                            style={{
                              fontSize: 14,
                              fontWeight: "600",
                              color: colors.ink,
                              flex: 1,
                            }}
                          >
                            {f.team} vs {f.opponent} · {f.date}
                          </Text>
                        </Pressable>
                      ))}
                    </>
                  )}
                </View>
              )}

              {/* Orphaned Teams */}
              <Pressable onPress={() => toggleSection("orphanedTeams")}>
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: "800",
                    color: colors.danger,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: 8,
                  }}
                >
                  {expandedSections.orphanedTeams ? "▼" : "▶"} ORPHANED TEAMS ({orphanedTeams.length})
                </Text>
              </Pressable>
              {expandedSections.orphanedTeams && (
                <View
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: radius.md,
                    borderWidth: 1,
                    borderColor: colors.line,
                    marginBottom: 16,
                  }}
                >
                  {orphanedTeams.length === 0 ? (
                    <View style={{ padding: 14 }}>
                      <Text style={{ fontSize: 13, color: colors.inkMuted }}>
                        No orphaned teams found
                      </Text>
                    </View>
                  ) : (
                    <>
                      <View
                        style={{
                          flexDirection: "row",
                          gap: 12,
                          padding: 12,
                          borderBottomWidth: 1,
                          borderBottomColor: colors.line,
                        }}
                      >
                        <Pressable
                          onPress={() =>
                            setSelectedOrphanedTeams(
                              new Set(orphanedTeams.map((t) => t.slug)),
                            )
                          }
                        >
                          <Text
                            style={{
                              fontSize: 12,
                              fontWeight: "700",
                              color: colors.brand,
                            }}
                          >
                            Select all
                          </Text>
                        </Pressable>
                        <Pressable
                          onPress={() => setSelectedOrphanedTeams(new Set())}
                        >
                          <Text
                            style={{
                              fontSize: 12,
                              fontWeight: "700",
                              color: colors.inkMuted,
                            }}
                          >
                            Deselect all
                          </Text>
                        </Pressable>
                      </View>
                      {orphanedTeams.map((t, i) => {
                        const selected = selectedOrphanedTeams.has(t.slug);
                        return (
                          <Pressable
                            key={t.slug}
                            onPress={() => {
                              setSelectedOrphanedTeams((prev) => {
                                const next = new Set(prev);
                                if (next.has(t.slug)) next.delete(t.slug);
                                else next.add(t.slug);
                                return next;
                              });
                            }}
                            style={{
                              padding: 12,
                              borderBottomWidth:
                                i < orphanedTeams.length - 1 ? 1 : 0,
                              borderBottomColor: colors.line,
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <View
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                borderWidth: 2,
                                borderColor: selected
                                  ? colors.danger
                                  : colors.line,
                                backgroundColor: selected
                                  ? colors.danger
                                  : "transparent",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {selected && (
                                <Text
                                  style={{
                                    color: "#fff",
                                    fontSize: 12,
                                    fontWeight: "800",
                                  }}
                                >
                                  ✓
                                </Text>
                              )}
                            </View>
                            <Text
                              style={{
                                fontSize: 14,
                                fontWeight: "600",
                                color: colors.ink,
                              }}
                            >
                              {t.name}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </>
                  )}
                </View>
              )}

              {/* Will Create */}
              <Pressable onPress={() => toggleSection("willCreate")}>
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: "800",
                    color: colors.brand,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: 8,
                  }}
                >
                  {expandedSections.willCreate ? "▼" : "▶"} WILL CREATE ({newFixtures.length})
                </Text>
              </Pressable>
              {expandedSections.willCreate && (
                <View
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: radius.md,
                    borderWidth: 1,
                    borderColor: colors.line,
                    marginBottom: 16,
                  }}
                >
                  {newFixtures.length === 0 ? (
                    <View style={{ padding: 14 }}>
                      <Text style={{ fontSize: 13, color: colors.inkMuted }}>
                        No new fixtures to create
                      </Text>
                    </View>
                  ) : (
                    newFixtures.map((f, i) => {
                      const isHome = myPcNames.includes(f.homeTeam);
                      const myPcTeam = isHome ? f.homeTeam : f.awayTeam;
                      const opp = stripToClubName(isHome ? f.awayTeam : f.homeTeam);
                      return (
                        <View
                          key={f.playCricketMatchId}
                          style={{
                            padding: 12,
                            borderBottomWidth:
                              i < newFixtures.length - 1 ? 1 : 0,
                            borderBottomColor: colors.line,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 14,
                              fontWeight: "600",
                              color: colors.ink,
                            }}
                          >
                            {teamMap[myPcTeam] || myPcTeam} vs {opp} · {f.date}
                          </Text>
                        </View>
                      );
                    })
                  )}
                </View>
              )}

              {/* Will Update */}
              <Pressable onPress={() => toggleSection("willUpdate")}>
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: "800",
                    color: colors.inkMuted,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: 8,
                  }}
                >
                  {expandedSections.willUpdate ? "▼" : "▶"} WILL UPDATE ({updateFixtures.length})
                </Text>
              </Pressable>
              {expandedSections.willUpdate && (
                <View
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: radius.md,
                    borderWidth: 1,
                    borderColor: colors.line,
                    marginBottom: 16,
                  }}
                >
                  {updateFixtures.length === 0 ? (
                    <View style={{ padding: 14 }}>
                      <Text style={{ fontSize: 13, color: colors.inkMuted }}>
                        No fixtures to update
                      </Text>
                    </View>
                  ) : (
                    updateFixtures.map((f, i) => {
                      const isHome = myPcNames.includes(f.homeTeam);
                      const myPcTeam = isHome ? f.homeTeam : f.awayTeam;
                      const opp = stripToClubName(isHome ? f.awayTeam : f.homeTeam);
                      return (
                        <View
                          key={f.playCricketMatchId}
                          style={{
                            padding: 12,
                            borderBottomWidth:
                              i < updateFixtures.length - 1 ? 1 : 0,
                            borderBottomColor: colors.line,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 14,
                              fontWeight: "600",
                              color: colors.ink,
                            }}
                          >
                            {teamMap[myPcTeam] || myPcTeam} vs {opp} · {f.date}
                          </Text>
                        </View>
                      );
                    })
                  )}
                </View>
              )}

              {/* Summary */}
              <View
                style={{
                  backgroundColor: colors.bg,
                  borderRadius: radius.md,
                  padding: 14,
                  marginBottom: 16,
                  marginTop: 8,
                }}
              >
                <Text
                  style={{
                    fontSize: 13,
                    color: colors.danger,
                    fontWeight: "700",
                    marginBottom: 4,
                  }}
                >
                  Deleting {selectedOrphanedFixtures.size} fixture{selectedOrphanedFixtures.size !== 1 ? "s" : ""}, {selectedOrphanedTeams.size} team{selectedOrphanedTeams.size !== 1 ? "s" : ""}
                </Text>
                <Text
                  style={{
                    fontSize: 13,
                    color: colors.brand,
                    fontWeight: "700",
                  }}
                >
                  Creating {newFixtures.length}, updating {updateFixtures.length}
                </Text>
              </View>

              {/* Action buttons */}
              <Pressable
                onPress={handleSyncAction}
                disabled={
                  syncing ||
                  (selectedOrphanedFixtures.size === 0 &&
                    selectedOrphanedTeams.size === 0)
                }
                style={({ pressed }) => ({
                  padding: 16,
                  borderRadius: radius.lg,
                  backgroundColor: colors.danger,
                  alignItems: "center",
                  marginBottom: 12,
                  opacity:
                    pressed ||
                    syncing ||
                    (selectedOrphanedFixtures.size === 0 &&
                      selectedOrphanedTeams.size === 0)
                      ? 0.6
                      : 1,
                })}
              >
                {syncing ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text
                    style={{ fontSize: 14, fontWeight: "700", color: "#fff" }}
                  >
                    Sync now
                  </Text>
                )}
              </Pressable>

              <Pressable
                onPress={handleImport}
                disabled={importing || fixturesForImport.length === 0}
                style={({ pressed }) => ({
                  padding: 16,
                  borderRadius: radius.lg,
                  backgroundColor: colors.brand,
                  alignItems: "center",
                  opacity: pressed || importing ? 0.8 : 1,
                })}
              >
                {importing ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text
                    style={{ fontSize: 14, fontWeight: "700", color: "#fff" }}
                  >
                    Just import (no deletions)
                  </Text>
                )}
              </Pressable>
            </>
          )}
        </View>

        <View style={{ height: 40 }} />
      </ContentWidth>
      <ConfirmSheet {...confirmProps} />
      <AlertSheet {...alertProps} />
    </ScrollView>
  );
}
